/*
 * Immersive quiz / routine builder.
 *
 * Every step and every possible result is already in the DOM, rendered by
 * sections/quiz-routine-builder.liquid. This only toggles `hidden`, keeps the
 * progress bar honest, and resolves which routine to show.
 *
 * Nothing boots from DOMContentLoaded: the theme editor replaces the section's
 * outerHTML on every edit, and a one-shot listener would leave dead HTML behind.
 * All state lives on the element and binds in connectedCallback.
 */

class QuizRoutineBuilder extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.stepCount = Math.max(1, this.steps.length);
    this.result = this.querySelector('[data-quiz-result]');
    this.routines = Array.from(this.querySelectorAll('[data-quiz-goal]'));
    this.counter = this.querySelector('[data-quiz-counter]');
    this.rail = this.querySelector('[data-quiz-rail]');
    this.actions = this.querySelector('[data-quiz-actions]');
    this.continueButton = this.querySelector('[data-quiz-continue]');
    this.backButton = this.querySelector('[data-quiz-back]');

    this.storageKey = this.dataset.storageKey;
    this.designMode = this.dataset.designMode === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.answers = {};
    this.stepIndex = 0;

    this.cacheNoteDefaults();
    this.bind();

    // restore() may land straight on the result screen, which render() would
    // immediately hide again.
    const resumedOnResult = this.designMode ? false : this.restore();
    if (!resumedOnResult) this.render({ focus: false });
  }

  disconnectedCallback() {
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  bind() {
    this.addEventListener('change', (event) => {
      if (!event.target.matches('.ap-quiz__option-input')) return;
      this.recordAnswer(event.target);
      this.updateContinueState();
      const error = event.target.closest('[data-quiz-step]').querySelector('[data-quiz-error]');
      if (error) error.hidden = true;
      this.save();
    });

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (target.closest('[data-quiz-continue]')) this.next();
      else if (target.closest('[data-quiz-back]')) this.back();
      else if (target.closest('[data-quiz-restart]')) this.restart();
      else if (target.closest('[data-quiz-exit]')) this.exit();
      else if (target.closest('[data-quiz-add]')) this.addRoutineToCart(target.closest('[data-quiz-add]'));
    });

    // The editor scrolls to a selected block; without this the client edits
    // step 4 and the preview keeps showing step 1.
    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const step = event.target.closest('[data-quiz-step]');
      if (step) {
        this.stepIndex = this.steps.indexOf(step);
        this.render({ focus: false, silent: true });
        return;
      }
      const routine = event.target.closest('[data-quiz-goal]');
      if (routine) this.showResult({ forced: routine, silent: true });
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  /* ------------------------------------------------------------- answers */

  get currentStep() {
    return this.steps[this.stepIndex];
  }

  questionId(step) {
    return step ? step.dataset.questionId : '';
  }

  // Insertion order matters: the matched formula is the first thing the owner
  // said they noticed, not the first option listed.
  recordAnswer(input) {
    const step = input.closest('[data-quiz-step]');
    if (!step) return;
    const id = this.questionId(step);
    const multi = step.dataset.multi === 'true';

    if (!multi) {
      this.answers[id] = [input.value];
      return;
    }

    const picked = this.answers[id] ? this.answers[id].slice() : [];
    const at = picked.indexOf(input.value);
    if (input.checked && at === -1) picked.push(input.value);
    if (!input.checked && at > -1) picked.splice(at, 1);
    this.answers[id] = picked;
  }

  hasAnswer(step) {
    const picked = this.answers[this.questionId(step)];
    return Array.isArray(picked) && picked.length > 0;
  }

  /* ---------------------------------------------------------- navigation */

  next() {
    const step = this.currentStep;
    if (!step) return;

    if (!this.hasAnswer(step)) {
      const error = step.querySelector('[data-quiz-error]');
      if (error) error.hidden = false;
      return;
    }

    if (this.stepIndex >= this.steps.length - 1) {
      this.showResult();
      return;
    }

    this.stepIndex += 1;
    this.render();
    this.save();
  }

  back() {
    if (this.stepIndex === 0) return;
    this.stepIndex -= 1;
    this.render();
    this.save();
  }

  restart() {
    this.answers = {};
    this.stepIndex = 0;
    this.querySelectorAll('.ap-quiz__option-input').forEach((input) => {
      input.checked = false;
    });
    this.result.hidden = true;
    this.render();
    this.clear();
  }

  render(options) {
    const settings = options || {};

    this.steps.forEach((step, index) => {
      step.hidden = index !== this.stepIndex;
      const error = step.querySelector('[data-quiz-error]');
      if (error) error.hidden = true;
    });

    if (this.result) this.result.hidden = true;
    this.setProgress(this.stepIndex + 1, 'Question ' + (this.stepIndex + 1) + ' of ' + this.stepCount, settings.silent);

    if (this.actions) this.actions.hidden = false;
    if (this.backButton) this.backButton.hidden = this.stepIndex === 0;
    this.updateContinueState();

    if (settings.focus !== false) this.focusPanel(this.currentStep);
  }

  // Styling hook only. The button stays genuinely clickable so that pressing it
  // unanswered surfaces the role=alert rather than doing nothing.
  updateContinueState() {
    if (!this.continueButton) return;
    this.continueButton.dataset.quizReady = this.hasAnswer(this.currentStep) ? 'true' : 'false';
  }

  setProgress(value, text, silent) {
    const percent = Math.round((value / this.stepCount) * 100);
    if (this.rail) {
      this.rail.style.setProperty('--apq-progress', percent + '%');
      this.rail.setAttribute('aria-valuenow', value);
      this.rail.setAttribute('aria-valuetext', text);
    }
    // Skipped in the editor so block selection does not spam the live region.
    if (this.counter && !silent) this.counter.textContent = text;
  }

  focusPanel(panel) {
    if (!panel) return;
    panel.focus();
    window.scrollTo({ top: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  /* -------------------------------------------------------------- result */

  cacheNoteDefaults() {
    this.notes = Array.from(this.querySelectorAll('[data-quiz-note]')).map((node) => {
      const key = node.dataset.quizNote;
      const alt = this.querySelector('[data-quiz-note-alt="' + key + '"]');
      return {
        node: node,
        key: key,
        base: node.textContent.trim(),
        alt: alt ? alt.content.textContent.trim() : '',
      };
    });
  }

  matchedRoutine() {
    const picked = this.answers.noticed || [];
    for (let i = 0; i < picked.length; i += 1) {
      const match = this.routines.find((routine) => routine.dataset.quizGoal === picked[i]);
      if (match) return match;
    }
    // Nothing matched, so recommend the foundation pair rather than a targeted
    // formula. This fell through to routines[0] -- whichever goal block happened
    // to be ordered first, currently Mobility -- which recommended a joint
    // formula to a dog with no joint symptoms. A foundation-only routine is the
    // honest answer to "nothing in particular", and now one exists.
    const foundationOnly = this.routines.find((routine) => !routine.dataset.targetVariant);
    return foundationOnly || this.routines[0];
  }

  doseLine() {
    const weightStep = this.steps.find((step) => this.questionId(step) === 'weight');
    if (!weightStep) return '';
    const checked = weightStep.querySelector('.ap-quiz__option-input:checked');
    const sub = checked ? checked.dataset.sub : '';
    return sub ? sub + ' daily' : '';
  }

  showResult(options) {
    const settings = options || {};
    const routine = settings.forced || this.matchedRoutine();
    if (!routine || !this.result) return;

    this.steps.forEach((step) => {
      step.hidden = true;
    });
    if (this.actions) this.actions.hidden = true;

    this.routines.forEach((node) => {
      node.hidden = node !== routine;
    });

    // A foundation-only routine carries a finished headline: there is no formula
    // name to substitute, and the template would render "..., plus ." without it.
    const title = this.result.querySelector('[data-quiz-result-title]');
    if (title && routine.dataset.resultTitle) {
      title.textContent = routine.dataset.resultTitle;
    } else if (title && title.dataset.titleTemplate) {
      title.textContent = title.dataset.titleTemplate.replace('[formula]', routine.dataset.targetName || '');
    }

    const dose = this.doseLine();
    routine.querySelectorAll('[data-quiz-dose]').forEach((node) => {
      node.textContent = dose;
    });

    const meds = this.answers.meds || [];
    const picky = this.answers.picky || [];
    this.notes.forEach((note) => {
      let useAlt = false;
      if (note.key === 'safety') useAlt = meds.includes('yes') || meds.includes('unsure');
      if (note.key === 'routine') useAlt = picky.includes('pills');
      note.node.textContent = useAlt && note.alt ? note.alt : note.base;
    });

    this.result.dataset.cadence = (this.answers.cadence || []).join(',');
    this.result.hidden = false;
    this.setProgress(this.stepCount, 'Your result', settings.silent);

    if (!settings.silent) this.focusPanel(this.result);
    this.save();
  }

  /* ---------------------------------------------------------------- cart */

  addRoutineToCart(button) {
    const routine = this.routines.find((node) => !node.hidden);
    const errorNode = this.querySelector('[data-quiz-cart-error]');
    if (!routine || button.getAttribute('aria-disabled') === 'true') return;

    // Both foundations, then the targeted formula if the routine has one. A
    // sold-out line is dropped rather than blocking the add; only an empty
    // basket is an error.
    //
    // "Monthly delivery" puts each line on the Recharge plan the Liquid read
    // from that variant's allocation. A line with no plan has no attribute and
    // goes in one-time: Shopify rejects a selling_plan the variant does not
    // carry, and dropping the bottle would be worse than not subscribing it.
    const subscribing = (this.answers.cadence || []).includes('sub');
    const items = [];
    const line = (variant, plan) => {
      if (!variant) return;
      const item = { id: Number(variant), quantity: 1 };
      if (subscribing && plan) item.selling_plan = Number(plan);
      items.push(item);
    };
    line(this.dataset.foundationVariant, this.dataset.foundationPlan);
    line(this.dataset.foundation2Variant, this.dataset.foundation2Plan);
    line(routine.dataset.targetVariant, routine.dataset.targetPlan);

    if (items.length === 0) {
      this.showCartError(errorNode, window.variantStrings ? window.variantStrings.soldOut : 'Sold out');
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];

    button.setAttribute('aria-disabled', 'true');
    if (errorNode) errorNode.hidden = true;

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          this.showCartError(errorNode, state.description || state.message);
          return;
        }
        if (cartDrawer) {
          cartDrawer.setActiveElement(button);
          cartDrawer.renderContents(state);
        } else {
          window.location = window.routes.cart_url;
        }
      })
      .catch(() => {
        this.showCartError(errorNode, window.cartStrings ? window.cartStrings.error : 'Something went wrong.');
      })
      .finally(() => {
        button.setAttribute('aria-disabled', 'false');
      });
  }

  showCartError(node, message) {
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  }

  /* ------------------------------------------------------------ exit/save */

  /*
   * "Save & exit" is the one way out that promises to keep the answers, and the
   * schema info under the button says so. Everything else saves too -- so a
   * crash, a refresh or a Back keeps its place -- but only this sets the flag
   * that survives walking back in through a CTA. See isFreshEntry().
   */
  exit() {
    this.save({ resume: true });
    // Navigating inside the editor iframe can drop the client out of the
    // template entirely, so the editor gets the save without the redirect.
    if (this.designMode) return;
    window.location.assign(this.dataset.exitUrl || '/');
  }

  save({ resume = false } = {}) {
    if (!this.storageKey || this.designMode) return;
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          step: this.stepIndex,
          answers: this.answers,
          done: this.result && !this.result.hidden,
          resume,
        })
      );
    } catch (error) {
      // Private browsing or a full quota: losing resume is not worth breaking on.
    }
  }

  /*
   * Whether the visitor arrived by following a link, rather than refreshing or
   * coming back.
   *
   * State is localStorage, so it outlives the tab and the browser. Someone who
   * abandoned the quiz a week ago and clicks "Take the quiz" was landing on
   * step 3 with their old answers pre-ticked, and the only "Start over" button
   * lives inside the result panel -- so a visitor who never finished could not
   * reach it. That is what QA reported.
   *
   * `navigation.type` is the thing that separates the two arrivals a stored
   * answer set means opposite things about. `navigate` is a fresh click from a
   * CTA or the menu; `reload` and `back_forward` are the same visit continuing,
   * and those still resume.
   *
   * Not a URL parameter, which was the obvious alternative. The top-menu entry
   * point is an Admin linklist (`main-menu-26`), not source -- it cannot be
   * given one, and it is the entry point QA named. A parameter would have fixed
   * the ten CTAs in this repo and missed that.
   *
   * No entry at all means an engine without the Navigation Timing API, and that
   * counts as fresh: starting over is the behaviour asked for, so it is the
   * safer end to err toward.
   */
  isFreshEntry() {
    const [nav] = window.performance?.getEntriesByType?.('navigation') || [];
    return !nav || nav.type === 'navigate';
  }

  clear() {
    if (!this.storageKey) return;
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // See save().
    }
  }

  // Returns true when the saved state resumed straight onto the result screen,
  // so connectedCallback knows not to re-render the question steps over it.
  restore() {
    if (!this.storageKey) return false;
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(this.storageKey));
    } catch (error) {
      return false;
    }
    if (!saved || !saved.answers) return false;

    // A fresh arrival starts over, unless the visitor used "Save & exit" -- and
    // that pass is spent on use, so the next arrival after it starts over like
    // any other.
    if (this.isFreshEntry()) {
      if (!saved.resume) {
        this.clear();
        return false;
      }
      delete saved.resume;
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(saved));
      } catch (error) {
        // See save(). A flag that cannot be cleared resumes once more, which is
        // a better failure than losing the answers it was protecting.
      }
    }

    this.answers = saved.answers;
    Object.keys(this.answers).forEach((id) => {
      const step = this.steps.find((node) => this.questionId(node) === id);
      if (!step) return;
      (this.answers[id] || []).forEach((value) => {
        const input = step.querySelector('.ap-quiz__option-input[value="' + CSS.escape(value) + '"]');
        if (input) input.checked = true;
      });
    });

    this.stepIndex = Math.min(Math.max(0, saved.step || 0), this.steps.length - 1);
    if (!saved.done) return false;

    this.showResult({ silent: true });
    return true;
  }
}

if (!customElements.get('quiz-routine-builder')) {
  customElements.define('quiz-routine-builder', QuizRoutineBuilder);
}
